<template>
  <div class="ec-event" :style="hasStyle ? style : null">
    <CPopover placement="right" trigger="click">
      <template #toggler="{ on }">
        <v-btn
          rounded="xl"
          variant="flat"
          size="x-small"
          :class="{ 'multiple-btn': is_multiple }"
          :color="`${getTitleColor(mainService)}`"
          v-on="on"
        >
          {{ event.customer.firstname }}
          {{ event.customer.lastname.charAt(0) }}
        </v-btn>
      </template>
      <template #content>
        <div class="popup-header" :class="`bg-${getTitleColor(mainService)}`">
          <div>{{ mainService.description }}</div>
          <div class="edit-btns">
            <v-btn
              variant="plain"
              density="compact"
              color="black"
              @click="editThisAppointment"
              title="Edit appointment"
              ><v-icon>mdi-square-edit-outline</v-icon></v-btn
            >
            <v-btn
              variant="tonal"
              density="compact"
              color="primary"
              @click="cloneThisAppointment"
              title="Copy appointment"
            >
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
            <v-btn
              variant="plain"
              density="compact"
              color="error"
              @click="searchThisAppointment"
              title="Add notes to client"
            >
              <v-icon>mdi-file-search-outline</v-icon>
            </v-btn>
            <v-btn
              variant="plain"
              density="compact"
              color="black"
              @click="deleteThisAppointment"
              title="Delete appointment"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </div>
        </div>
        <div class="service-chips">
          <v-chip
            v-for="(ser, index) in eventServiceList"
            :key="index"
            size="x-small"
            :class="`bg-${getTitleColor(ser)}`"
          >
            {{ ser.description }}
          </v-chip>
        </div>
        <v-list class="v-card-overlay-jobs">
          <v-list-item
            density="compact"
            :title="name"
            prepend-icon="mdi-account"
          ></v-list-item>
          <v-list-item
            density="compact"
            :title="event.customer.phone"
            prepend-icon="mdi-phone"
          ></v-list-item>
          <v-list-item
            density="compact"
            :title="event.customer.email"
            prepend-icon="mdi-email-outline"
          ></v-list-item>
        </v-list>
        <v-card-text class="bg-grey-lighten-3 text-body-2">
          <div class="appt-note">Note: {{ event.note }}</div>
        </v-card-text>
      </template>
    </CPopover>
    <CancelAppointmentDialog
      v-model="showCancelDialog"
      @confirm="confirmDelete"
    />
  </div>
</template>

<script setup>
import { CPopover } from "@coreui/vue";
import "@coreui/coreui/dist/css/coreui.min.css";
import { computed, ref } from "vue";
import { getTitleColor, calculateTopMargin } from "./../libs/util";
import { useEditStore } from "./../stores/EditFormStore";
import CancelAppointmentDialog from "./dialogs/CancelAppointmentDialog.vue";

const emit = defineEmits(["deleteEvent", "searchEvent"]);
const props = defineProps({
  date: {
    type: Object,
    default: null,
  },
  event: {
    type: Object,
    default: () => {},
  },
  eventIndex: {
    type: Number,
    default: 0,
  },
  hasStyle: {
    type: Boolean,
    default: true,
  },
});

const editStore = useEditStore();
const showCancelDialog = ref(false);
let slotHeight = 50;

// start is a timestamp as setHours returns timestamp
// let start = props.date;
// start.setUTCHours(8, 0, 0);
// let diff = props.event.datetime.getTime() - start;
// let slots = diff / 1000 / 60 / 15;
// let offset = Math.round(slots);
// let _top = offset * slotHeight + props.eventIndex * 25 + 50;
let top = calculateTopMargin(props.date, props.event, props.eventIndex);
let style = "top: " + top + "px; position:absolute;";

const eventServiceList = computed(() => {
  let services = Object.assign([], props.event.services);
  services.sort((a, b) => a.id - b.id);
  services.shift();
  return services;
});

const mainService = computed(() => {
  let services = Object.assign([], props.event.services);
  services.sort((a, b) => a.id - b.id);
  return services.shift();
});

let name = computed(() => {
  return props.event.customer.firstname + " " + props.event.customer.lastname;
});

let is_multiple = computed(() => {
  return props.event.services.length !== 1;
});

function editThisAppointment() {
  if (!editStore.isEditMode) {
    editStore.setIsEditMode();
  }
  editStore.setEditFormId(props.event.id);
  editStore.setIsCloneMode(false);
}

function cloneThisAppointment() {
  if (!editStore.isEditMode) {
    editStore.setIsEditMode();
  }
  editStore.setEditFormId(props.event.id);
  editStore.setIsCloneMode(true);
}

function deleteThisAppointment() {
  showCancelDialog.value = true;
}

function confirmDelete(cancellationNote) {
  emit("deleteEvent", props.event.id, cancellationNote);
}

function searchThisAppointment() {
  emit("searchEvent", props.event.id);
}
</script>

<style>
.ec-event {
  margin-left: 8px;
}
.popover {
  width: 350px;
  --cui-popover-max-width: 350px;
  max-width: 350px !important;
}
.popover-body {
  padding: 0px;
}

.popup-header {
  padding: 10px 15px 10px 10px;
  box-sizing: border-box;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  font-weight: 500;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  gap: 8px;
}

.v-list-item__prepend > .v-icon {
  -webkit-margin-end: 16px;
  margin-inline-end: 16px;
}
.v-list-item-title {
  font-size: 14px;
}

.edit-btns {
  margin-left: auto;
  display: flex;
  gap: 4px;
}

.edit-btns .v-btn {
  padding: 0;
  max-width: 25px;
  background-color: #d3d3d3;
  width: 26px;
  min-width: 26px;
  height: 26px;
}

.multiple-btn {
  border: 3px solid pink;
}

.service-chips {
  padding: 8px 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.v-card-overlay-jobs {
  padding: 0 4px;
}

.appt-note {
  word-wrap: break-word;
}
/* .multiple-block::after {
  content: "\2795";
  font-size: 9px;
  position: relative;
  left: -3px;
  top: -2px;
  color: pink !important;
} */
</style>
